// This file serves homepages and section fronts created after Feb. 2024.
// it is in the process of being refactored and will be replaced by
// /pages/curation/published/[pageId]/index.tsx and the preview version
// at /pages/curation/preview/[pageId]/index.tsx. - BW 2/5/2025
import React, { useMemo } from "react";
import nr from "newrelic";
import type { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import type { NextParsedUrlQuery } from "next/dist/server/request-meta";
import Head from "next/head";
import {
    ContainerWithParallaxLeaderboard,
    ProtoContainerWithParallaxLeaderboard,
} from "@bloomberg/media-ui-ads";
import type { TickerSecurity } from "@bloomberg/media-ui-markets/dist/types";
import {
    type MenuItems,
    type SubmenuItem,
    type BannerData,
} from "@bloomberg/media-ui-nav/types/types";
import GoogleOneTap from "@foundation/google-one-tap";
import Logger from "@foundation/logger";
import {
    StaticMetadata,
    OpenGraph,
    TwitterMetadata,
    ParselyMetadata,
    Favicon,
} from "@foundation/metadata";
import { NavProvider, type REGIONS } from "@foundation/nav";
import { Tophat, SkipLinkTarget } from "@foundation/nextjs-tophat";
import { QuickAddProvider } from "@foundation/quickadd-provider";
import { ScreenProvider } from "@foundation/screen";
import AdOptionsProvider from "@foundation/sparkle/lib/AdOptionsProvider";
import type { Targeting } from "@foundation/sparkle/lib/AdTargeting/TargetingProvider";
import WinSparkleProvider from "@foundation/sparkle/lib/WinSparkleProvider";
import { TrackingProvider } from "@foundation/with-tracking";

import {
    LAZY_LOAD_STARTING_ZONES,
    PAGE_TYPES,
    PUBLISHED_STATE,
    PHOENIX_PREFIX,
} from "~constants";
import { getAdConfig, type AdConfig } from "~api/getAdConfig";
import { NewSearchTip } from "~components/search/popover/NewSearchTip";
import Footer from "~components/shared/footer";
import NavContainer from "~components/shared/nav";
import PhoenixTickerBarWrapper from "~components/shared/phoenixTickerBarWrapper";
import { PlugScript } from "~components/shared/plugClient";
import PreviewErrorMessage from "~components/shared/preview-error-message/PreviewErrorMessage";
import { Zones } from "~components/shared/zones";
import { PageContext } from "~context/PageContext";
import { createUnexpectedParamsError } from "~errors/errors";
import type { LabelIds } from "~i18n/types";
import pageType from "~models/transforms/pageType";
import useRedux from "~redux/hooks";
import { getTranslationsViewModel } from "~server/bff/surface/translations";
import type {
    Curation,
    PageData,
    ModulesById,
    NotFound,
    AnalyticsData,
} from "~types/types";
import type { Locale } from "~types/wardenclyffe";
import { getAdTargeting } from "~utils/AdTargetingClient";
import {
    getPhoenixTophat,
    getMenuItems,
    getBreakingNews,
    getFooterMenuItems,
    getTickerBar,
    getStationIDFromPath,
} from "~utils/commonPageResources";
import { handlePageError } from "~utils/errorHandler";
import { fetchCurationAndHydrate } from "~utils/fetchHydratedContent";
import type { WardenclyffeUserParams } from "~utils/fetchWardenclyffe";
import { getBreakpointMap } from "~utils/getBreakpointMap";
import getCuratedPageAnalytics from "~utils/getCuratedPageAnalytics";
import {
    getLeaderboardAdOptions,
    getSparkleProviderOptions,
} from "~utils/getSparkleProviderOptions";
import { isInvalidResourceValue } from "~utils/isInvalidResourceValue";
import isInvalidTestQuery from "~utils/isInvalidTestQuery";
import isProtoParallaxLeaderboardEnabled from "~utils/isProtoParallaxLeaderboardEnabled";
import { getCurrentUrl } from "~utils/nextContextUtils";
import sizeImage from "~utils/sizeImage";

const WATCHLIST_SERVICE_URL = `${process.env.NEXT_PUBLIC_REGISTRATION_URL}/watchlist-service`;
interface NewHomePageProps {
    initialState: {
        curation: Curation | null;
        modulesById: ModulesById | null;
        pageData: PageData;
        tophatSnippet: string;
        navMenuItems: {
            menuItems: MenuItems;
            footerMenuItems: Record<string, SubmenuItem>;
        };
        breakingNews: BannerData;
        tickerBar: TickerSecurity[];
        channel: string;
    };
    locale: Locale;
    translatedMessages: Record<keyof LabelIds, string>;
    analytics: AnalyticsData;
    disableLazyLoading: boolean;
    region?: REGIONS;
    countryCode?: string;
    adTargetings?: Targeting;
    adConfig: AdConfig;
}

export interface NewHomePageParams extends NextParsedUrlQuery {
    pageId: string;
}

export interface NewHomePageQuery extends NextParsedUrlQuery {
    inPreview?: string;
    disableLazyLoading?: string;
    channelName?: string;
}

export interface NewHomePageGetServerSidePropsContext
    extends GetServerSidePropsContext {
    params: NewHomePageParams;
    query: NewHomePageQuery;
    locale: Locale;
}

const breakpoints = getBreakpointMap([
    { breakpoint: "mob-grid", name: "mobile" },
    { breakpoint: "tab-grid", name: "tablet" },
    { breakpoint: "xsdt-grid", name: "xSmallDesktop" },
    { breakpoint: "sdt-grid", name: "smallDesktop" },
    { breakpoint: "ldt-grid", name: "largeDesktop" },
]);

const adBreakpoints = {
    mobile: 0,
    tablet: 768,
    small_desktop: 1123,
    large_desktop: 1335,
};

type NewHomePageGetServerSideProps<
    P extends NewHomePageProps = NewHomePageProps
> = (
    context: NewHomePageGetServerSidePropsContext
) => Promise<GetServerSidePropsResult<P>> | NotFound;

const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

export const getServerSideProps:
    | NewHomePageGetServerSideProps
    | unknown = async (context: NewHomePageGetServerSidePropsContext) => {
    const {
        params,
        query,
        locale,
        req: { cookies },
        res,
    } = context;

    // Validate the page ID before this step
    // We need to guarantee the shape of NewHomePageParams or serve an error
    const preview = Boolean(query.inPreview === "true");

    // Disabling lazyLoading in all cases because nestedModules in lazy loaded
    // zones do not work. - BW 3/6/2025
    const disableLazyLoading = true;
    const publishedState = preview
        ? PUBLISHED_STATE.DRAFT
        : PUBLISHED_STATE.PUBLISHED;
    const pageId = params?.pageId;
    const region = cookies?.exp_pref as REGIONS;
    const countryCode = cookies?.country_code || "US";
    const errorPolicy = "all";

    nr.addCustomAttributes({
        ...params,
        ...query,
        locale,
    });

    if (isInvalidResourceValue(pageId) || isInvalidTestQuery(query)) {
        const error = createUnexpectedParamsError(
            `Invalid page id for curation: ${pageId}`
        );

        nr.noticeError(error, true);
        console.warn(error);

        return { notFound: true };
    }

    const userParams: WardenclyffeUserParams = {
        preview,
        locale,
    };

    Logger.info(`${pageId} - Lineup`);

    const translatedMessages = await getTranslationsViewModel({
        userParams,
        errorPolicy,
    });

    let curationResponse;

    try {
        curationResponse = await fetchCurationAndHydrate(
            pageId,
            publishedState,
            locale,
            preview,
            disableLazyLoading
        );
    } catch (error: unknown) {
        return handlePageError({
            error,
            res,
            isPreview: preview,
            newrelicParams: { ...query, publishedState, preview },
        });
    }

    const { modules, curation, pageData } = curationResponse;

    return Promise.all([
        getPhoenixTophat(),
        getBreakingNews(region, publishedState),
        getFooterMenuItems(),
        getMenuItems(),
        getTickerBar(true, pageId, locale),
        getAdTargeting({
            pageId,
            adCode: pageData.adCode,
            adPageType: "other",
            url: getCurrentUrl(context),
        }),
    ])
        .then(
            ([
                tophatSnippet,
                breakingNews,
                footerMenuItems,
                menuItems,
                tickerBar,
                adTargetings,
                ,
            ]) => {
                const analytics = getCuratedPageAnalytics(pageData);
                const liveRadio = getStationIDFromPath(query.channelName);

                return {
                    props: {
                        initialState: {
                            curation: curation || null,
                            tophatSnippet,
                            breakingNews,
                            navMenuItems: {
                                menuItems,
                                footerMenuItems,
                            },
                            modulesById: modules || null,
                            pageData,
                            tickerBar,
                            ...(pageId === "phx-new-audio" && {
                                // this will add liveRadio initial redux state based on path
                                liveRadio: {
                                    schedule: {
                                        id: liveRadio.scheduleId,
                                        loading: true,
                                        data: [],
                                        error: null,
                                    },
                                    station: liveRadio.radioStation,
                                    currentEpisodeIndex: -1,
                                },
                            }),
                        },
                        locale,
                        translatedMessages,
                        analytics,
                        disableLazyLoading,
                        countryCode,
                        adConfig: getAdConfig(),
                        adTargetings,
                    },
                };
            }
        )
        .catch((error) => {
            const errorDetails = {
                message: "Unable to retrieve page data",
                params,
                query,
                error,
            };
            Logger.error(errorDetails);

            nr.noticeError(
                error as Error,
                {
                    ...query,
                    publishedState,
                    preview,
                },
                true
            );

            throw new Error(error);
        });
};

interface ConditionalTrackingProps {
    isPreview: boolean;
}

function ConditionalTracking(
    props: React.PropsWithChildren<ConditionalTrackingProps>
) {
    const { isPreview, children } = props;

    if (isPreview) {
        return <>{children}</>;
    }

    const trackingData = {
        count: 0,
        pageviewId: "",
    };

    return (
        <TrackingProvider addTrackingAnchor trackingData={trackingData}>
            {children}
        </TrackingProvider>
    );
}

export default function NewHomePage({
    disableLazyLoading,
    region,
    countryCode,
    adTargetings,
    adConfig,
}: NewHomePageProps) {
    const { useAppSelector } = useRedux();

    const curation = useAppSelector((state) => state.curation);
    const pageData = useAppSelector((state) => state.pageData);
    const tophatSnippet = useAppSelector((state) => state.tophatSnippet);
    const tickerBar = useAppSelector((state) => state.tickerBar);
    const menuItems = useAppSelector((state) => state.navMenuItems.menuItems);

    const footerMenuItems = useAppSelector(
        (state) => state.navMenuItems.footerMenuItems
    );
    const breakingNews = useAppSelector((state) => state.breakingNews);

    const { pageId, adCode, title, path, description, preview } = pageData;

    const { brand } = curation;

    const pageIdForAds = pageId.startsWith(PHOENIX_PREFIX)
        ? pageId.slice(PHOENIX_PREFIX.length)
        : pageId;

    const sparkleOptions = useMemo(
        () =>
            getSparkleProviderOptions(
                pageIdForAds,
                adCode,
                adBreakpoints,
                adTargetings
            ),
        [pageId, adCode, adTargetings]
    );

    const pageContextValue = useMemo(
        () => ({ useGrid: true, isPreview: preview }),
        []
    );

    if (!curation) {
        return null;
    }

    const ParallaxContainerLeaderboard = isProtoParallaxLeaderboardEnabled(
        pageId
    )
        ? ProtoContainerWithParallaxLeaderboard
        : ContainerWithParallaxLeaderboard;

    const initialZones = disableLazyLoading
        ? curation.zones
        : curation.zones.slice(0, LAZY_LOAD_STARTING_ZONES);
    const lazyZones = disableLazyLoading
        ? []
        : curation.zones.slice(LAZY_LOAD_STARTING_ZONES);
    const shareImage = sizeImage(curation?.image?.baseUrl || "", 1200, 630);
    const url = `https://www.bloomberg.com${path}`;

    const trackingPageType = preview
        ? `lineup-curation-preview`
        : `lineup-curation`;

    const isHomepage = pageType(pageId).data === PAGE_TYPES.home;
    const isPreviewErrorState =
        preview && Boolean(curation.isPreviewLastGoodCopy);
    const leaderBoardAdOptions = getLeaderboardAdOptions(pageId);

    return (
        <div data-page-type={trackingPageType}>
            <Head>
                <title key="title">{title}</title>
                <Favicon brand={brand} env={COLOR_ENV} />
                <StaticMetadata canonical={url} description={description} />
                <ParselyMetadata
                    title={title}
                    link={url}
                    type="sectionpage"
                    image={shareImage}
                />
                <OpenGraph
                    description={description}
                    title={title}
                    type="website"
                    url={url}
                    image={shareImage}
                />
                <TwitterMetadata
                    twitterHandle="@business"
                    description={description}
                    title={title}
                    image={shareImage}
                />
            </Head>
            <ConditionalTracking isPreview={pageData.preview}>
                <WinSparkleProvider {...sparkleOptions}>
                    <AdOptionsProvider adOptions={adConfig.globalAdOptions}>
                        {isPreviewErrorState && <PreviewErrorMessage />}
                        <ParallaxContainerLeaderboard
                            isDecoupled={leaderBoardAdOptions.isDecoupled}
                            hideEmptyAd={leaderBoardAdOptions.hideEmptyAd}
                        >
                            <Tophat snippet={tophatSnippet} />
                            <NavProvider
                                environment={
                                    COLOR_ENV === "production"
                                        ? "production"
                                        : "staging"
                                }
                            >
                                <NavContainer
                                    breakingNews={breakingNews}
                                    menuItems={menuItems}
                                    useGrid
                                    region={region}
                                    countryCode={countryCode}
                                />
                            </NavProvider>
                            {isHomepage && (
                                <>
                                    <GoogleOneTap
                                        style={{
                                            position: "absolute",
                                            right: "30px",
                                            marginTop: "5px",
                                            zIndex: "2",
                                        }}
                                    />
                                    <NewSearchTip />
                                </>
                            )}
                            {tickerBar && (
                                <PhoenixTickerBarWrapper
                                    tickerBar={tickerBar}
                                    pageId={pageId}
                                />
                            )}
                            <PlugScript />
                            <QuickAddProvider
                                watchlistServiceUrl={WATCHLIST_SERVICE_URL}
                            >
                                <ScreenProvider breakpoints={breakpoints}>
                                    <PageContext.Provider
                                        value={pageContextValue}
                                    >
                                        <SkipLinkTarget />
                                        <AdOptionsProvider
                                            adOptions={adConfig.globalAdOptions}
                                        >
                                            <Zones
                                                initialZones={initialZones}
                                                lazyZones={lazyZones}
                                            />
                                        </AdOptionsProvider>
                                    </PageContext.Provider>
                                </ScreenProvider>
                            </QuickAddProvider>
                            <Footer footerMenuItems={footerMenuItems} />
                        </ParallaxContainerLeaderboard>
                    </AdOptionsProvider>
                </WinSparkleProvider>
            </ConditionalTracking>
        </div>
    );
}
